import { useEffect, useRef } from "react";
import {
  useGetUserStoresLazyQuery,
  useUpsertUserStoreMutation,
} from "@api/graphql/generated/generated-types";
import { useAuth } from "@src/common/hooks/useAuth";
import { Theme, useTheme } from "./ThemeProvider";

const THEME_STORE_KEY = "theme";
const VALID_THEMES = new Set<string>([Theme.AUTO, Theme.LIGHT, Theme.DARK]);

// Syncs theme with the user preference (UserStore) backend.
// Must be called inside AuthProvider since it depends on useAuth.
export function useThemeUserPrefSync() {
  const { courierId, employeeId, courieUser } = useAuth();
  const userOrEmployeeId = employeeId || courieUser?.id;
  const { theme, setTheme } = useTheme();

  const [getUserStores] = useGetUserStoresLazyQuery({
    fetchPolicy: "network-only",
  });
  const [upsertUserStore] = useUpsertUserStoreMutation();

  // Lets async callbacks see the latest theme without re-running the effect.
  const themeRef = useRef<Theme>(theme);
  useEffect(() => {
    themeRef.current = theme;
  }, [theme]);

  // Hydration is tracked per userOrEmployeeId so a same-session re-login as a
  // different user refetches that user's preference. `inflightFor` covers the
  // window between fetch start and resolve to avoid duplicate fetches.
  const hydratedFor = useRef<string | null>(null);
  const inflightFor = useRef<string | null>(null);
  const lastWrittenTheme = useRef<Theme | null>(null);

  useEffect(() => {
    if (!courierId || !userOrEmployeeId) return;
    if (hydratedFor.current === userOrEmployeeId) return;
    if (inflightFor.current === userOrEmployeeId) return;

    const targetUser = userOrEmployeeId;
    const themeAtStart = theme;
    inflightFor.current = targetUser;
    lastWrittenTheme.current = null;

    getUserStores({
      variables: {
        courierId,
        userOrEmployeeId: targetUser,
        keysFilter: [THEME_STORE_KEY],
      },
    }).then((res) => {
      // Another user took over while we were waiting — discard this result.
      if (inflightFor.current !== targetUser) return;
      inflightFor.current = null;
      hydratedFor.current = targetUser;

      // User toggled mid-flight — honor their choice and persist it instead of
      // overwriting it with the stored value we just fetched.
      if (themeRef.current !== themeAtStart) {
        lastWrittenTheme.current = themeRef.current;
        upsertUserStore({
          variables: {
            courierId,
            userOrEmployeeId: targetUser,
            key: THEME_STORE_KEY,
            value: themeRef.current,
          },
        });
        return;
      }

      const stored = res.data?.userStores?.find(
        (entry) => entry.key === THEME_STORE_KEY
      )?.value;
      if (stored && VALID_THEMES.has(stored)) {
        lastWrittenTheme.current = stored as Theme;
        setTheme(stored as Theme);
      } else {
        lastWrittenTheme.current = themeAtStart;
        upsertUserStore({
          variables: {
            courierId,
            userOrEmployeeId: targetUser,
            key: THEME_STORE_KEY,
            value: themeAtStart,
          },
        });
      }
    });
  }, [courierId, userOrEmployeeId]);

  useEffect(() => {
    if (!courierId || !userOrEmployeeId) return;
    // Wait until THIS user's hydration is complete before persisting changes.
    if (hydratedFor.current !== userOrEmployeeId) return;
    if (lastWrittenTheme.current === theme) return;
    lastWrittenTheme.current = theme;
    upsertUserStore({
      variables: {
        courierId,
        userOrEmployeeId,
        key: THEME_STORE_KEY,
        value: theme,
      },
    });
  }, [theme, courierId, userOrEmployeeId]);
}
